import Immutable from 'immutable';
import {
  CANCEL_LOGIN,
  CLEAR_RETRIES_QUEUE,
  FORGOT_PASSWORD,
  LOGIN_REQUIRED,
  LOGIN_START,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  FACEBOOK_LOGIN_START,
  FACEBOOK_LOGIN_SUCCESS,
  FACEBOOK_LOGIN_FAILURE,
  LOGOUT_START,
  LOGOUT_SUCCESS,
  LOGOUT_FAILURE,
  RESET_PASSWORD_START,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAILURE,
  SET_CURRENT_USER,
  SET_CURRENT_USER_ACCESS,
  SIGNUP_FAILURE,
  SIGNUP_START,
  AUTH_SET_CURRENT_PORTAL_NAME,
  AUTH_SET_CURRENT_COURSE,
  AUTH_SET_CURRENT_COHORT,
  SET_LEGACY_TOKEN,
  SET_SUBDOMAIN_COOKIE,
  START_FETCH_ENROLLED_CLASS_DATA,
  UPDATE_ENROLLED_CLASS_DATA,
  SSO_USER_CONTEXT,
  CURRENT_SSO_USER_DETAILS,
  SIGNUP_USER_REQUIRED,
  SET_LOGIN_DATE,
  SET_LOGIN_MODE,
  CANCEL_FORGOT_PASSWORD,
} from './actions';
import { FETCH_INSTITUTION_ROLES_SUCCESS } from '../Institutions/actions';
import { READ_BROWSER_CONTEXT } from '../location/actions';

const initialState = Immutable.fromJS({
  currentUser: null,
  forgotPassword: false,
  loginRequired: false,
  inProgress: false,
  errorMessage: null,
  retriesQueue: [],
  legacyToken: null,
  lastLoginDate: null,

  isSuperUser: false,
  courseRoles: {}, // coursePath: ['admin', ...]
  classRoles: {}, // cohortPath: [roleName, ...]
  institutionRoles: {},
  numFetchingEnrolledClassData: 0,
  hasFetchedEnrolledClassData: false,
  customDomain: Immutable.Map(),
});

/**
 * @param {object} state
 * @param {object} action
 * @returns {object}
 */
export function Auth(state = initialState, action = {}) {
  switch (action.type) {
    case CANCEL_LOGIN: {
      if (!state.get('inProgress')) {
        let newState = state.set('loginRequired', false);
        newState = newState.set('retriesQueue', Immutable.List());
        return newState;
      }
      return state;
    }
    case CLEAR_RETRIES_QUEUE: {
      return state.set('retriesQueue', Immutable.List());
    }
    case FORGOT_PASSWORD: {
      if (!state.get('inProgress')) {
        return state.set('forgotPassword', true);
      }
      return state;
    }
    case LOGIN_REQUIRED: {
      let newState = state.set('loginRequired', true);
      newState = newState.set('forgotPassword', false);

      if (action.request) {
        const retriesQueue = newState.get('retriesQueue');
        newState = newState.set(
          'retriesQueue',
          retriesQueue.push(action.request)
        );
      }
      newState = newState.set('loginMode', action.mode);
      return newState;
    }
    case SET_LOGIN_MODE: {
      const { mode } = action;
      if (state.get('inProgress')) {
        // don't allow changing of tabs while login/signup is in progress
        return state;
      }
      return state.set('loginMode', mode);
    }
    case FACEBOOK_LOGIN_START:
    case LOGIN_START:
    case LOGOUT_START:
    case RESET_PASSWORD_START:
    case SIGNUP_START: {
      let newState = state;
      newState = newState.set('inProgress', true);
      newState = newState.set('errorMessage', null);
      return newState;
    }
    case FACEBOOK_LOGIN_SUCCESS: {
      let newState = state;
      newState = newState.set('currentUser', action.userId);
      newState = newState.set('loginRequired', false);
      newState = newState.set('inProgress', false);
      return newState;
    }
    case LOGIN_SUCCESS: {
      let newState = state;
      newState = newState.set('currentUser', action.user.userId);
      newState = newState.set('loginRequired', false);
      newState = newState.set('inProgress', false);
      return newState;
    }
    case LOGOUT_SUCCESS: {
      return initialState;
    }
    case RESET_PASSWORD_SUCCESS: {
      return state.set('inProgress', false);
    }
    case CANCEL_FORGOT_PASSWORD: {
      if (state.get('inProgress')) {
        // don't allow cancelling if in the middle of resetting password
        return state;
      }
      // this should end up going back to the login tab
      return state.set('forgotPassword', false);
    }
    case FACEBOOK_LOGIN_FAILURE:
    case LOGIN_FAILURE:
    case LOGOUT_FAILURE: {
      return state.set('inProgress', false);
    }
    case RESET_PASSWORD_FAILURE:
    case SIGNUP_FAILURE: {
      let newState = state;
      newState = newState.set('inProgress', false);
      newState = newState.set('errorMessage', action.errorMessage);
      return newState;
    }
    case SET_CURRENT_USER: {
      return state.set('currentUser', action.currentUser);
    }
    case SET_CURRENT_USER_ACCESS: {
      let newState = state;

      newState = newState.set('isSuperUser', !!action.access.isSuperUser); // force undefined to be boolean
      newState = newState.set(
        'courseRoles',
        newState.get('courseRoles').merge(action.access.courseRoles)
      );
      newState = newState.set(
        'classRoles',
        newState.get('classRoles').merge(action.access.classRoles)
      );
      newState = newState.set(
        'institutionRoles',
        newState.get('institutionRoles').merge(action.access.institutionRoles)
      );
      newState = newState.set(
        'currentInstitution',
        action.access.currentInstitution
      );

      return newState;
    }
    case FETCH_INSTITUTION_ROLES_SUCCESS: {
      const { institutionRoles } = action;
      return state.set(
        'institutionRoles',
        state.get('institutionRoles').merge(institutionRoles)
      );
    }
    case AUTH_SET_CURRENT_PORTAL_NAME: {
      return state.set('currentPortalName', action.currentPortalName);
    }
    case AUTH_SET_CURRENT_COURSE: {
      return state.set('currentCourse', action.coursePath.toLowerCase());
    }
    case AUTH_SET_CURRENT_COHORT: {
      return state.set('currentCohort', action.cohortPath.toLowerCase());
    }
    case READ_BROWSER_CONTEXT: {
      return state.set('csrfToken', action.csrfToken);
    }
    case START_FETCH_ENROLLED_CLASS_DATA: {
      return state.set(
        'numFetchingEnrolledClassData',
        state.get('numFetchingEnrolledClassData') + 1
      );
    }
    case UPDATE_ENROLLED_CLASS_DATA: {
      return state
        .set(
          'numFetchingEnrolledClassData',
          state.get('numFetchingEnrolledClassData') - 1
        )
        .set('hasFetchedEnrolledClassData', true);
    }
    case SET_LEGACY_TOKEN: {
      let newState = state;
      newState = newState.set('legacyToken', action.legacyToken);
      newState = newState.set('customDomain', Immutable.Map());
      return newState;
    }
    case SET_SUBDOMAIN_COOKIE: {
      let newState = state;
      const { url, legacyToken } = action;
      newState = newState.setIn(['customDomain', url], legacyToken);
      return newState;
    }
    case SSO_USER_CONTEXT: {
      return state
        .set('ssoContext', action.ssoContext)
        .set('ssoUserDefaultImg', action.ssoUserDefaultImg);
    }
    case SIGNUP_USER_REQUIRED: {
      return state.set('signUpUserRequired', action.signUpUserRequired);
    }
    case CURRENT_SSO_USER_DETAILS: {
      return state.set(
        'currentUserSSODetails',
        Immutable.fromJS(action.currentUserSSODetails)
      );
    }
    case SET_LOGIN_DATE: {
      let newState = state;
      newState = newState.set('lastLoginDate', action.lastLoginDate);
      return newState;
    }
    default: {
      return state;
    }
  }
}
